// src/features/dashboard/teacher/ImportStudentsModal.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { fetchDistinctClasses, fetchStudentsByClass, importStudentsToClass } from '../../../services/sheetDataService';

const ImportStudentsModal = ({
  isOpen,
  onClose,
  currentClass,    // null when used in new class creation
  targetMetadata,  // provided during new class creation
  onImportComplete // callback receives the selected students (for new class flow)
}) => {
  const { accessToken, user } = useAuth();
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [sourceStudents, setSourceStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  // Helper: get the correct spreadsheet ID for user data
  const getUsersSheetId = () => user.usersSpreadsheetId || user.spreadsheetId;

  // Load classes when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const loadClasses = async () => {
      try {
        const usersSheetId = getUsersSheetId();
        const classes = await fetchDistinctClasses(accessToken, usersSheetId);
        const filtered = currentClass
          ? classes.filter(cls =>
              cls.subject !== currentClass.subject || cls.grade !== currentClass.grade
            )
          : classes;
        setAvailableClasses(filtered);
      } catch (err) {
        console.error('Failed to load classes:', err);
      }
    };
    loadClasses();
    setSelectedClass(null);
    setSourceStudents([]);
    setSelectedStudents({});
  }, [isOpen, accessToken, user, currentClass]);

  // Load students when a source class is selected
  useEffect(() => {
    const loadStudents = async () => {
      if (!selectedClass || !accessToken || !user?.spreadsheetId) return;
      setLoading(true);
      try {
        const usersSheetId = getUsersSheetId();
        const students = await fetchStudentsByClass(
          accessToken,
          usersSheetId,
          selectedClass.subject,
          selectedClass.grade
        );
        setSourceStudents(students);
        const initialSelection = {};
        students.forEach(s => { initialSelection[s.userId] = true; });
        setSelectedStudents(initialSelection);
      } catch (err) {
        console.error('Failed to load source students:', err);
      } finally {
        setLoading(false);
      }
    };
    loadStudents();
  }, [selectedClass, accessToken, user]);

  const toggleSelectAll = (checked) => {
    const newSelection = {};
    sourceStudents.forEach(s => { newSelection[s.userId] = checked; });
    setSelectedStudents(newSelection);
  };

  const toggleStudent = (userId) => {
    setSelectedStudents(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const handleImport = async () => {
    const studentsToImport = sourceStudents.filter(s => selectedStudents[s.userId]);
    if (studentsToImport.length === 0) {
      alert('Please select at least one student to import.');
      return;
    }

    // 🆕 NEW CLASS FLOW: just return students to the form, no sheet write
    if (!currentClass) {
      if (onImportComplete) {
        onImportComplete(studentsToImport);
      }
      onClose();
      return;
    }

    // ✅ EXISTING CLASS FLOW: write to sheet immediately
    setImporting(true);
    try {
      const usersSheetId = getUsersSheetId();
      const orgMetadata = {
        organisationId: user.organisationId || '',
        school: user.school || '',
        province: user.province || '',
        district: user.district || '',
        circuit: user.circuit || ''
      };

      const target = {
        teacher: currentClass.email || user.email,
        subject: currentClass.subject,
        grade: currentClass.grade,
        googleClassroomLink: currentClass.googleClassroomLink || '',
        schedule: currentClass.schedule || [],
        zoomLink: currentClass.zoomLink || '',
        zoomPassword: currentClass.zoomPassword || ''
      };

      const result = await importStudentsToClass(
        accessToken,
        usersSheetId, // 👈 use users sheet ID
        studentsToImport,
        target,
        orgMetadata
      );

      alert(`✅ Import complete: ${result.added} students added, ${result.skipped} skipped (already in class).`);
      if (onImportComplete) {
        onImportComplete();
      }
      onClose();
    } catch (err) {
      alert('❌ Failed to import students: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">
          {currentClass ? 'Import Students' : 'Select Students to Import'}
        </h2>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Source Class</label>
          <select
            value={selectedClass ? `${selectedClass.subject}|${selectedClass.grade}` : ''}
            onChange={(e) => {
              const [subject, grade] = e.target.value.split('|');
              const cls = availableClasses.find(c => c.subject === subject && c.grade === grade);
              setSelectedClass(cls || null);
            }}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">Select a class</option>
            {availableClasses.map(cls => (
              <option key={`${cls.subject}|${cls.grade}`} value={`${cls.subject}|${cls.grade}`}>
                {cls.subject} - {cls.grade}
              </option>
            ))}
          </select>
          {availableClasses.length === 0 && (
            <p className="text-sm text-gray-500 mt-1">No other classes available to import from.</p>
          )}
        </div>

        {selectedClass && (
          <div>
            {loading ? (
              <p className="text-center py-4">Loading students...</p>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={sourceStudents.length > 0 && sourceStudents.every(s => selectedStudents[s.userId])}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <label className="text-sm font-medium">Select All ({sourceStudents.length} students)</label>
                </div>

                <div className="border rounded max-h-60 overflow-y-auto">
                  {sourceStudents.map(student => (
                    <div key={student.userId} className="flex items-center gap-2 p-2 border-b hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={!!selectedStudents[student.userId]}
                        onChange={() => toggleStudent(student.userId)}
                        className="h-4 w-4"
                      />
                      <span>{student.name} {student.surname}</span>
                      <span className="text-sm text-gray-500 ml-2">{student.email}</span>
                    </div>
                  ))}
                  {sourceStudents.length === 0 && (
                    <p className="p-4 text-gray-500">No students found in this class.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            disabled={importing}
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!selectedClass || importing || sourceStudents.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {importing ? 'Importing...' : 'Import Selected Students'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportStudentsModal;